import type {
  DefaultResponseBody,
  ListResponseBody,
  Podcast,
  PodcastDetail,
  PodcastEpisode,
  PodcastEpisodeDetail,
  PodcastEpisodeListParams,
  PodcastListParams,
} from "@gerama/types";
import type { ApiClient } from "../client";

const empty = { list: [], count: 0 };

export function createCorePodcastsApi(client: ApiClient) {
  return {
    list: (params: PodcastListParams = {}, signal?: AbortSignal) =>
      client
        .request<ListResponseBody<Podcast>>("/podcasts", { query: { ...params }, signal })
        .then((res) => res.data ?? empty),

    followed: (params: { page?: number; limit?: number } = {}, signal?: AbortSignal) =>
      client
        .request<ListResponseBody<Podcast>>("/podcasts/followed", { query: { ...params }, signal })
        .then((res) => res.data ?? empty),

    detail: (id: number, signal?: AbortSignal) =>
      client
        .request<DefaultResponseBody<PodcastDetail>>(`/podcasts/${id}`, { signal })
        .then((res) => res.data as PodcastDetail),

    episodes: (id: number, params: PodcastEpisodeListParams = {}, signal?: AbortSignal) =>
      client
        .request<ListResponseBody<PodcastEpisode>>(`/podcasts/${id}/episodes`, {
          query: { ...params },
          signal,
        })
        .then((res) => res.data ?? empty),

    latestEpisodes: (params: PodcastEpisodeListParams = {}, signal?: AbortSignal) =>
      client
        .request<ListResponseBody<PodcastEpisode>>("/podcasts/episodes", {
          query: { ...params },
          signal,
        })
        .then((res) => res.data ?? empty),

    episode: (id: number, signal?: AbortSignal) =>
      client
        .request<DefaultResponseBody<PodcastEpisodeDetail>>(`/podcasts/episodes/${id}`, { signal })
        .then((res) => res.data as PodcastEpisodeDetail),

    follow: (id: number) =>
      client
        .request<DefaultResponseBody<{ isFollowing: boolean; followerCount: number }>>(
          `/podcasts/${id}/follow`,
          {
            method: "POST",
          },
        )
        .then((res) => res.data),

    unfollow: (id: number) =>
      client
        .request<DefaultResponseBody<{ isFollowing: boolean; followerCount: number }>>(
          `/podcasts/${id}/follow`,
          {
            method: "DELETE",
          },
        )
        .then((res) => res.data),

    saveProgress: (episodeId: number, positionSeconds: number, completed?: boolean) =>
      client.request<DefaultResponseBody<null>>(`/podcasts/episodes/${episodeId}/progress`, {
        method: "PUT",
        body: { positionSeconds: Math.floor(positionSeconds), completed },
      }),

    streamUrl: (episodeId: number) => `${client.baseUrl}/podcasts/episodes/${episodeId}/stream`,
  };
}
